import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Eager-load ALL jpg/jpeg/png/webp assets so new uploads are auto-discovered
const allAssets = import.meta.glob('@/assets/*.{jpg,jpeg,png,webp}', { eager: true, as: 'url' });

// Resolve by filename — returns '' if not yet uploaded (new images)
const a = (name: string): string => {
  const key = Object.keys(allAssets).find(k => k.endsWith(`/${name}`));
  return key ? (allAssets[key] as string) : '';
};

// Exact order as specified — unknowns are filtered out at runtime
const slides = [
  a('ziko-marketplace-hero.jpg'), // hero-1
  a('hero-2.jpg'),
  a('hero-3333.webp'),
  a('hero-3.jpg'),
  a('hero-333.webp'),
  a('hero-44.webp'),
  a('20.webp'),
  a('hero-4.jpg'),
  a('hero-5.webp'),
  a('hero-6.jpg'),
  a('hero-7.jpg'),
  a('hero-55.jpg'),
  a('hero-11.jpg'),
  a('hero-10.webp'),
  a('hero-88.jpg'),
].filter(Boolean);

const INTERVAL      = 3200;  // faster — was 5000ms
const FADE_DURATION = 1.2;   // snappier crossfade — was 1.6

const HeroSection = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // First paint shouldn't fade in from opacity:0 — that was directly
  // delaying Largest Contentful Paint by the full ~1.2s transition
  // duration, since the browser doesn't count the image as "painted" in a
  // visually meaningful way until the fade finishes. Every later carousel
  // transition still gets the nice fade; only the initial load skips it.
  const isFirstPaint = useRef(true);

  const goTo = useCallback((index: number) => {
    setPrev(current);
    setCurrent(index);
  }, [current]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  const previous = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  // Auto-advance
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, INTERVAL);
  }, [next]);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resetTimer]);

  // Flip the first-paint flag shortly after mount — separate from
  // onAnimationComplete since that doesn't reliably fire when
  // initial={false} skips the animation entirely (nothing to "complete").
  useEffect(() => {
    const t = setTimeout(() => { isFirstPaint.current = false; }, 100);
    return () => clearTimeout(t);
  }, []);

  const handlePrev = () => { previous(); resetTimer(); };
  const handleNext = () => { next(); resetTimer(); };
  const handleDot  = (i: number) => { goTo(i); resetTimer(); };

  return (
    <section className="relative min-h-[90vh] pt-16 flex items-center justify-center overflow-hidden bg-black">

      {/* Previous slide — fades out */}
      {prev !== null && (
        <motion.img
          key={`prev-${prev}`}
          src={slides[prev]}
          alt=""
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: FADE_DURATION, ease: 'easeInOut' }}
          onAnimationComplete={() => setPrev(null)}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      )}

      {/* Current slide — fades & subtly zooms in (except on first paint) */}
      <motion.img
        key={`slide-${current}`}
        src={slides[current]}
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
        initial={isFirstPaint.current ? false : { opacity: 0, scale: 1.06 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: FADE_DURATION, ease: 'easeInOut' }}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-background/65 gradient-mesh" />

      {/* Arrows */}
      <button
        onClick={handlePrev}
        className="absolute left-4 z-20 p-2 rounded-full bg-background/30 backdrop-blur-sm text-foreground hover:bg-background/55 transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={handleNext}
        className="absolute right-4 z-20 p-2 rounded-full bg-background/30 backdrop-blur-sm text-foreground hover:bg-background/55 transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 z-20 flex gap-1">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDot(i)}
            aria-label={`Go to slide ${i + 1}`}
            className="h-9 w-9 flex items-center justify-center"
          >
            <span className={`h-2 rounded-full transition-all duration-500 block ${
              i === current ? 'bg-primary w-6' : 'bg-foreground/30 w-2 hover:bg-foreground/50'
            }`} />
          </button>
        ))}
      </div>

      {/* Hero text */}
      <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-xs uppercase tracking-[0.3em] text-primary mb-4"
        >
          {t('hero.tagline')}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6"
        >
          {t('hero.title1')}{' '}
          <span className="text-gradient">{t('hero.titleHighlight')}</span>
          <br />{t('hero.title2')}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-muted-foreground max-w-lg mx-auto mb-8"
        >
          {t('hero.description')}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors neon-glow"
          >
            {t('hero.shopNow')} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;




